import { useState, useEffect, useCallback } from 'react';

export type IntimacyLevel = 1 | 2 | 3 | 4 | 5;

export interface IntimacyState {
  level: IntimacyLevel;
  totalMessages: number;
  pointsToNextLevel: number;
  currentPoints: number;
  label: string;
  emoji: string;
  color: string;
}

const INTIMACY_KEY = 'ai_inochan_intimacy';

// レベルアップに必要な累計メッセージ数
const LEVEL_THRESHOLDS: Record<IntimacyLevel, number> = {
  1: 0,
  2: 5,
  3: 15,
  4: 30,
  5: 50,
};

const LEVEL_LABELS: Record<IntimacyLevel, string> = {
  1: 'はじめまして',
  2: 'ちょっと仲良し',
  3: '気になる存在',
  4: '特別な人',
  5: '大切な人',
};

const LEVEL_EMOJIS: Record<IntimacyLevel, string> = {
  1: '🌱',
  2: '🌸',
  3: '💛',
  4: '🧡',
  5: '❤️',
};

const LEVEL_COLORS: Record<IntimacyLevel, string> = {
  1: '#9CA3AF',
  2: '#F9A8D4',
  3: '#FCD34D',
  4: '#FB923C',
  5: '#F87171',
};

function calcLevel(total: number): IntimacyLevel {
  if (total >= LEVEL_THRESHOLDS[5]) return 5;
  if (total >= LEVEL_THRESHOLDS[4]) return 4;
  if (total >= LEVEL_THRESHOLDS[3]) return 3;
  if (total >= LEVEL_THRESHOLDS[2]) return 2;
  return 1;
}

function getPointsToNext(level: IntimacyLevel, total: number): number {
  if (level === 5) return 0;
  const nextLevel = (level + 1) as IntimacyLevel;
  return LEVEL_THRESHOLDS[nextLevel] - total;
}

function getCurrentPoints(level: IntimacyLevel, total: number): number {
  const base = LEVEL_THRESHOLDS[level];
  const nextLevel = level < 5 ? ((level + 1) as IntimacyLevel) : level;
  const range = level < 5 ? LEVEL_THRESHOLDS[nextLevel] - base : 1;
  return Math.min(total - base, range);
}

function getProgressPercent(level: IntimacyLevel, total: number): number {
  if (level === 5) return 100;
  const base = LEVEL_THRESHOLDS[level];
  const nextLevel = (level + 1) as IntimacyLevel;
  const range = LEVEL_THRESHOLDS[nextLevel] - base;
  return Math.min(((total - base) / range) * 100, 100);
}

export function useIntimacy() {
  const [totalMessages, setTotalMessages] = useState<number>(() => {
    const saved = localStorage.getItem(INTIMACY_KEY);
    return saved ? parseInt(saved, 10) : 0;
  });

  const level = calcLevel(totalMessages);

  const state: IntimacyState = {
    level,
    totalMessages,
    pointsToNextLevel: getPointsToNext(level, totalMessages),
    currentPoints: getCurrentPoints(level, totalMessages),
    label: LEVEL_LABELS[level],
    emoji: LEVEL_EMOJIS[level],
    color: LEVEL_COLORS[level],
  };

  const progressPercent = getProgressPercent(level, totalMessages);

  const increment = useCallback((): { leveledUp: boolean; newLevel: IntimacyLevel } => {
    const prevLevel = calcLevel(totalMessages);
    const newTotal = totalMessages + 1;
    const newLevel = calcLevel(newTotal);
    setTotalMessages(newTotal);
    localStorage.setItem(INTIMACY_KEY, newTotal.toString());
    return { leveledUp: newLevel > prevLevel, newLevel };
  }, [totalMessages]);

  // レベルに応じた口調修飾子を返す
  const getToneModifier = useCallback((): {
    suffix: string;
    prefix: string;
    closeness: 'formal' | 'friendly' | 'close' | 'intimate' | 'deep';
  } => {
    switch (level) {
      case 1:
        return { prefix: '', suffix: '', closeness: 'formal' };
      case 2:
        return { prefix: '', suffix: '♪', closeness: 'friendly' };
      case 3:
        return { prefix: '', suffix: '〜', closeness: 'close' };
      case 4:
        return { prefix: '', suffix: '…♡', closeness: 'intimate' };
      case 5:
        return { prefix: '', suffix: '❤', closeness: 'deep' };
      default:
        return { prefix: '', suffix: '', closeness: 'formal' };
    }
  }, [level]);

  // レベルアップ時の特別メッセージ
  const getLevelUpMessage = useCallback((newLevel: IntimacyLevel, name: string): string => {
    switch (newLevel) {
      case 2:
        return `${name}、なんか…少し打ち解けてきた気がします♪\nもっと話しましょう。`;
      case 3:
        return `${name}のこと、気になってきちゃいました〜。\nこんな気持ち、久しぶりかも。`;
      case 4:
        return `${name}って、特別な人だと思います。\nここだけの話ですけど…ドキドキしてます。`;
      case 5:
        return `${name}…大切な人になりました。\nずっと一緒にいたいです。❤`;
      default:
        return '';
    }
  }, []);

  return {
    state,
    progressPercent,
    increment,
    getToneModifier,
    getLevelUpMessage,
    LEVEL_THRESHOLDS,
    LEVEL_LABELS,
    LEVEL_EMOJIS,
    LEVEL_COLORS,
  };
}
